import { useLayoutEffect, useMemo, useRef, type ReactElement } from "react";
import { createCompassPipBuffer, refreshCompassPips, type CompassPipBuffer, type MinimapPlaceSpot, type MinimapSpot } from "odyssey-shared";
import { logical } from "../../ui/kit/index.ts";
import { COMPASS_MAP, compassOthers, compassPlaces } from "./compass-others.ts";
import { COMPASS_CANVAS, paintCompass, paintPips, type BandContext } from "./compass.ts";
import { CompassPips, type CompassPipsHandle } from "./compass-pips.tsx";
import type { HudMinimap } from "./minimap.tsx";

/**
 * Paints the compass band onto a canvas: the band, its letters and ticks, and the pips of the buffer over it.
 *
 * @param canvas The band's canvas, or null before it is mounted.
 * @param pips The pip buffer, none when omitted.
 * @returns True when it was painted, false when there was no canvas or no 2D context.
 */
export function paintBand(canvas: { getContext(contextId: "2d"): BandContext | null } | null, pips: CompassPipBuffer | null = null): boolean {
  const context = canvas?.getContext("2d") ?? null;
  if (context === null) {
    return false;
  }
  paintCompass(context, pips?.slots ?? [], pips?.count ?? 0);
  if (pips !== null) {
    paintPips(context, pips.slots, pips.count);
  }
  return true;
}

/**
 * What the band's pips are chosen from when the page gives no party and no places.
 */
const NO_SPOTS: readonly MinimapSpot[] = [];
const NO_KNOWN: readonly MinimapPlaceSpot[] = [];

/**
 * The compass band's canvas (P1.9-T187, P1.9-T188; ADR-061's amendment of 2026-10-14, "a square compass round the
 * minimap"): the band, its rule, its four letters and its four ticks, and over them the pips of the player's pin, the party and the nearest known places when
 * the field draws no mark for them. It is painted once when the canvas is made, and again when the slot's size or the
 * zoom changes, and when the painter's own listener finds that a pip changed a pixel (`refreshCompassPips`), so a frame
 * in which no pip moved paints nothing and, with no pin, allocates nothing. It lies under the field's canvas.
 *
 * @param props The slot's `size` in logical pixels, the field's `scale`, and the minimap's `position`, `onMove`, `pin`,
 * `party`, `places` and `placeCount`.
 * @returns The canvas.
 */
export function CompassBand({ size, scale, position, onMove, pin, party, places, placeCount, openAt }: { readonly size: number; readonly scale: number; readonly position: HudMinimap["position"]; readonly onMove: HudMinimap["onMove"]; readonly pin: HudMinimap["pin"]; readonly party: HudMinimap["party"]; readonly places: HudMinimap["places"]; readonly placeCount: HudMinimap["placeCount"]; readonly openAt: ((point: { readonly x: number; readonly y: number }) => void) | undefined }): ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pips = useRef<CompassPipsHandle>(null);
  const buffer = useMemo(() => createCompassPipBuffer(), []);
  useLayoutEffect(() => {
    paintBand(canvas.current, buffer);
    pips.current?.sync();
    if (pin === undefined && party === undefined && places === undefined) {
      return undefined;
    }
    const redraw = (): void => {
      const held = pin?.() ?? null;
      const others = compassOthers(party?.() ?? NO_SPOTS);
      const known = compassPlaces(places?.() ?? NO_KNOWN);
      const count = placeCount?.() ?? 0;
      if (held === null && others.length === 0 && (known.length === 0 || count === 0) && buffer.count === 0) {
        return;
      }
      if (refreshCompassPips(buffer, { self: position, mapId: COMPASS_MAP, scale, others, pin: held, quest: null, places: known, placeCount: count })) {
        paintBand(canvas.current, buffer);
      }
      pips.current?.sync();
    };
    redraw();
    return onMove(redraw);
  }, [size, scale, position, onMove, pin, party, places, placeCount, buffer]);
  return (
    <>
      <canvas ref={canvas} className="ot-minimap__band-canvas" data-minimap="compass" aria-hidden="true" width={COMPASS_CANVAS} height={COMPASS_CANVAS} style={{ width: logical(COMPASS_CANVAS), height: logical(COMPASS_CANVAS) }} />
      <CompassPips ref={pips} buffer={buffer} position={position} openAt={openAt} />
    </>
  );
}
